<!--
// nuxt-ui/pages/[region]/MediaAccreditationInfo.vue
-->
<script setup lang="ts">
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/media-accreditation",
    es: "/[region]/acreditaciones-prensa",
    de: "/[region]/medien-akkreditierung",
    fr: "/[region]/accreditation-presse",
    it: "/[region]/accrediti-media",
    pt: "/[region]/acreditacao-media",
  },
});

definePageMeta({
  middleware: [
    "not-found-king-of-kings-nations",
    "not-found-brazil",
    "not-found-nations",
    "not-found-world-cup",
    "not-found-france",
  ],
});

const { t } = useI18n();
const { isSpain, isItaly, isAmericas, isGermany } = useRegions();
const { isKings } = useWebContext();
const { region } = useCompetitionStore();

useSeoMeta({
  title: t("footer.nav.media"),
  ogTitle: t("footer.nav.media"),
});
</script>

<template>
  <section class="py-9 px-6 md:py-24 max-w-4xl md:mx-auto">
    <div v-if="isItaly">
      <i18n-t keypath="mediaAccreditation.title" tag="h1" class="text-gray-300 text-2xl leading-9 mb-4">
        <template #leagueTitle>
          <span>{{ region?.leagueTitle }}</span>
        </template>
      </i18n-t>

      <i18n-t keypath="mediaAccreditation.italy.text.p1" tag="p" class="my-6">
        <template #italyMediaEmail>
          <a class="text-secondary hover:underline" :href="`mailto:${$t('mediaAccreditation.italy.email')}`">
            {{ $t("mediaAccreditation.italy.email") }}
          </a>
        </template>
      </i18n-t>

      <p class="my-6">{{ $t("mediaAccreditation.italy.text.p2") }}</p>
      <p class="mt-6 mb-3">{{ $t("mediaAccreditation.italy.text.p3") }}</p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <li>{{ $t("mediaAccreditation.italy.text.list1") }}</li>
        <li>{{ $t("mediaAccreditation.italy.text.list2") }}</li>
        <li>{{ $t("mediaAccreditation.italy.text.list3") }}</li>
        <li>{{ $t("mediaAccreditation.italy.text.list4") }}</li>
        <li>{{ $t("mediaAccreditation.italy.text.list5") }}</li>
      </ul>
    </div>

    <div v-else-if="isSpain">
      <i18n-t keypath="mediaAccreditation.title" tag="h1" class="text-gray-300 text-2xl leading-9 mb-4">
        <template #leagueTitle>
          <span>{{ $t("mediaAccreditation.spain.leagues") }}</span>
        </template>
      </i18n-t>

      <i18n-t keypath="mediaAccreditation.spain.text.p1" tag="p" class="my-6">
        <template #leagues>
          <em>{{ $t("mediaAccreditation.spain.leagues") }}</em>
        </template>
        <template #strongLimitDayHour>
          <strong>{{ $t("mediaAccreditation.spain.limitDayHour") }}</strong>
        </template>
        <template #spainMediaEmail>
          <a class="text-secondary hover:underline" :href="`mailto:${$t('mediaAccreditation.spain.email')}`">
            {{ $t("mediaAccreditation.spain.email") }}
          </a>
        </template>
      </i18n-t>

      <i18n-t keypath="mediaAccreditation.spain.text.p2" tag="p" class="my-6">
        <template #cupraArenaLink
          ><a
            class="text-secondary hover:underline"
            href="https://maps.app.goo.gl/RXAzZ3UWM4wgjveZ7"
            target="_blank"
            >{{ $t("mediaAccreditation.spain.cupraArenaText") }}</a
          ></template
        >
      </i18n-t>

      <p class="mt-6 mb-3">{{ $t("mediaAccreditation.spain.text.p3") }}</p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <i18n-t keypath="mediaAccreditation.spain.text.list1" tag="li">
          <template #strongPressTribune>
            <strong>{{ $t("mediaAccreditation.spain.pressTribune") }}</strong>
          </template>
        </i18n-t>
        <i18n-t keypath="mediaAccreditation.spain.text.list2" tag="li">
          <template #validity>
            <strong>{{ $t("mediaAccreditation.spain.validity") }}</strong>
          </template>
        </i18n-t>
      </ul>

      <p class="mt-6 mb-3">{{ $t("mediaAccreditation.spain.text.p4") }}</p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <li>{{ $t("mediaAccreditation.spain.text.list3") }}</li>
        <li>{{ $t("mediaAccreditation.spain.text.list4") }}</li>
        <li>{{ $t("mediaAccreditation.spain.text.list5") }}</li>
        <li>{{ $t("mediaAccreditation.spain.text.list6") }}</li>
        <li>{{ $t("mediaAccreditation.spain.text.list8") }}</li>
      </ul>

      <p class="mt-6 mb-3">{{ $t("mediaAccreditation.spain.text.p5") }}</p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <li>{{ $t("mediaAccreditation.spain.text.list9") }}</li>
        <li>{{ $t("mediaAccreditation.spain.text.list10") }}</li>
      </ul>

      <p class="my-6">{{ $t("mediaAccreditation.spain.text.p6") }}</p>
    </div>

    <div v-else-if="isAmericas">
      <i18n-t keypath="mediaAccreditation.title" tag="h1" class="text-gray-300 text-2xl leading-9 mb-4">
        <template #leagueTitle>
          <span>{{ region?.leagueTitle }}</span>
        </template>
      </i18n-t>

      <p class="my-6">
        Los medios de comunicación son bienvenidos en todas las jornadas de nuestros splits y eventos
        especiales que tenemos. Tratamos de dar cabida a todos y todas pero es importante considerar que se
        cuenta con una limitante de espacio.
      </p>

      <p class="my-6">
        Las solicitudes de acreditación
        <strong>se aperturan los días lunes y se cierran a las 12 horas (CDMX) de los días jueves</strong>.
      </p>

      <p class="my-6">
        Si hay algún evento disponible podrás encontrar el formato de solicitud
        <a
          href="https://qr-code.click/i/p/67b50979ae35a"
          target="_blank"
          class="text-secondary hover:underline"
          >aquí</a
        >.
      </p>
    </div>

    <div v-else-if="isGermany">
      <h1 class="text-gray-300 text-2xl leading-9 mb-4">
        Akkreditierungsanfrage für die Presse – Kings League Germany Qualifier
      </h1>
      <p class="my-6">
        Alle Akkreditierungsanfragen für die Kings League Germany Qualifier (einschließlich Journalist:innen,
        Fotograf:innen, digitale Creator und Social Media Manager) müssen bis spätestens Donnerstag vor dem
        jeweiligen Spieltag um 23:59 Uhr an folgende Adresse gesendet werden:
        <a href="mailto:media.ger@kingsleague.pro" target="_blank" class="text-secondary hover:underline"
          >media.ger@kingsleague.pro</a
        >
      </p>
      <p class="my-6">Die Spiele finden in der <strong>CUPRA Arena – MMC Studios Köln statt</strong>.</p>
      <p class="mt-6 mb-3"><strong>Wichtige Informationen:</strong></p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <li>
          Die Akkreditierung gewährt den Zugang zur Pressetribüne und ist ausschließlich für das im Antrag
          angegebene Datum gültig.
        </li>
        <li>
          Bitte beachten Sie, dass Akkreditierungen jeweils nur für einen einzelnen Spieltag vergeben werden.
          Das bedeutet: Die Akkreditierung gilt ausschließlich für den von Ihnen im Antrag genannten Tag – und
          nicht automatisch für weitere Spieltage.
        </li>
        <li>
          Beispiel: Wenn Sie eine Akkreditierung für den 1. Spieltag (Samstag, 12. April) beantragen, ist
          diese auch nur für diesen Tag gültig – nicht für den 2. Spieltag am Sonntag, 13. April.
        </li>
      </ul>

      <p class="mt-6 mb-3"><strong>Für die Akkreditierung benötigen wir folgende Angaben:</strong></p>
      <ul class="list-disc list-outside pl-8 mb-6">
        <li>Vollständiger Name</li>
        <li>Medium / Redaktion</li>
        <li>E-Mail-Adresse</li>
        <li>Spiel, an dem Sie teilnehmen möchten</li>
      </ul>
    </div>
  </section>
</template>

<style scoped></style>
